import { RawPaginatable } from 'interfaces/table'
import { Device, PasswordStrengthPayload } from 'interfaces/auth'
import { NetworthSummaryResponse, NetworthFilterPreferencesApiPayload } from 'interfaces/networth'
import { City, Location } from 'interfaces/locations'
import { CategoryId, RawMetaData } from 'interfaces/meta'
import { Member, MemberPreferencesApiPayload, MemberPreferencesApiResponse, RawMemberWithProps } from 'interfaces/member'
import { ConfirmCreditFormData, CreditAnswer, CreditTotals, RawCreditAccount } from 'interfaces/credit'
import { AccountRate, AggregatedBalanceByCategory, RawAllDetailedAccountsDict } from 'interfaces/accounts'
import { Insight, InsightCategoriesApiFilter, PlacementType, InsightId } from 'interfaces/insights'
import {
  MoneyflowPeergroupBreakdownResponseItem,
  Peergroup,
  PeergroupsTotals,
  RawPeergroupAccountsBreakdown,
  RawPeergroupRelation,
} from 'interfaces/peergroups'
import { Car, CarMake, CarModel, Home, HomeFormValues, HomePriceResponse, RawCarFormValues, RawHomeFormValues } from 'interfaces/property'
import { MoneyflowBreakdownItem, MoneyflowTracker, PatchTransactionResponse, PatchTransactionAPIPayload, RawTransaction } from 'interfaces/moneyflow'
import {
  DateParam,
  GetParamsUrl,
  GroupParam,
  GroupsWithDateParam,
  GroupsWithPeriodParam,
  PeriodParam,
  PostBodyUrl,
  PostQueryBodyUrl,
  RelationsParam,
  ScaledPeriodParam,
  SimpleUrl,
  UrlWithId,
} from 'interfaces/ajax/base'
import { PeergroupTrend, Trend } from 'interfaces/trend'
import {
  BaseProvider,
  DetailedProvider,
  LiveUpdateInfo,
  LiveUpdateInfoByStatus,
  RawManualBalanceAccount,
  RawMixedProviderAccount,
  RawProviderAccount,
  RawProviderBalanceAccount,
} from 'interfaces/linkage'
import { Offer } from 'interfaces/offers'
import { PlainEvent, PostImpressionsArray } from 'components/features/beacon/types'
import {
  CreateProviderAccountApiPayload,
  EncryptedCredentials,
  MixedAccountOperationApiResponse,
  GetTransactionsApiPayload,
  OffsetPaginationApiPayload,
  PaginatableApiPayload,
  OrderingApiPayload,
} from 'interfaces/ajax/payload'
import {
  Accomplishmentable,
  GetRedemptionAccountResponse,
  GetRedemptionsApiPayload,
  GetRewardsApiPayload,
  Redemption,
  RedemptionFormResponse,
  RedemptionFormValues,
  Reward,
  ReferralInfo,
} from 'interfaces/bounty'
import { RewardsHistoryItem } from 'components/features/rewards/rewardPage/store'
import { AnyDict, AnyObj } from 'interfaces/index'
import { ImageURLs, MediaLink } from 'interfaces/media'
import {
  CountData,
  FeedPostInput,
  GetPostsApiPayload,
  PostUid,
  RawPost,
  FeedUser,
  FeedCommentInput,
  FollowedPostsCount,
  FeedFilterQuery,
  FeedFilterStatus,
  RawComment,
  GetCommentsApiPayload,
  GetFollowedUsersApiPayload,
  RawFollowedUser,
  GetFollowedTagsApiPayload,
  FeedTag,
  FeedTagName,
  RawOwnComment,
  RawFeedUser,
  SuggestUser,
  SuggestTag,
  CheckExistsRequest,
  CheckExistsResponse,
} from 'interfaces/feed'
import { GetFeedTagsRequest } from 'actions/feedActions'
import { GetSocialInsightsApiPayload, RawSocialInsight } from 'interfaces/socialInsights'
import {
  PublicTrendApiPayload,
  PublicTrendState,
  PublicBreakdownByCategoryApiPayload,
  PublicBreakdownWithTotal,
  PublicBreakdownByStateApiPayload,
  PublicBreakdownByMerchantApiPayload,
  PublicBreakdown,
} from 'interfaces/publicDashboard'
import { SuggestUsersApiPayload } from 'actions/feedComposeActions'
import { Subscription, ExtendedSubscription, PostSubscriptionStripeApiPayload } from 'interfaces/subscription'
import {
  GetFutureApiResponse,
  FutureLayer,
  FutureLayerFields,
  FutureAssumptions,
  FutureLayerCheckResult,
  FutureLayerSlug,
  FutureAssumptionsTaxHints,
  FutureLayerUID,
  CompleteLayer,
  FutureLayerDefinedHints,
} from 'components/features/future/types'
import { AdvisorApiOutput, CreateAdvisorMeetingApiInput } from 'components/features/coaching/advisorMeeting/types/requests'
import { AdvisorTopics } from 'components/features/coaching/advisorMeeting/fixtures/requestQuestions'
import { MeetingApiOutput, MeetingUid } from 'components/features/coaching/advisorPage/types/interfaces'
import { MeetingVideoDetailsApiOutput } from 'components/features/coaching/advisorVideoCall/types/state'
import { AdvisorsPatchReviewPayload } from 'components/features/coaching/advisorStore/actions/advisorMeetingActions'
import { AdvisorUid, AdvisorWithRatingBreakdown, RawAdvisorReview, Advisor } from 'components/features/coaching/advisor/types/advisor'
import { GetPublicAdvisorReviewsPayload } from 'components/features/coaching/advisorStore/actions/publicAdvisorActions'
import { CryptoRewardsGetEndpoints, CryptoRewardsPostEndpoints } from 'components/features/rewards/rewardsPane/types/endpoints'
import { GetRewardSummaryResponse } from 'components/features/rewards/rewardPage/store'
import { CreditCardGetEndpoints, CreditCardPatchEndpoints, CreditCardPostEndpoints } from 'components/features/creditCard/creditCardStore/endpoints'

// Peergroups
export const GET_PEER_GROUP_RELATIONS_URL = '/api/v1/member-peergroup-relations/' as const
export const GET_PEER_GROUP_TOTAL_TOTALS_URL = '/api/v1/peergroups/totals/' as const

export const CREATE_PEER_GROUP_URL = '/api/v1/member-peergroup-relations/create/' as const
export const RANDOMIZE_PEER_GROUP_URL = '/api/v1/member-peergroup-relations/randomize/' as const
export const UPDATE_PEER_GROUP_URL = '/api/v1/member-peergroup-relations/:id/update/' as const
export const DELETE_PEER_GROUP_URL = '/api/v1/member-peergroup-relations/:id/delete/' as const
export const FIND_PEER_GROUP_URL = '/api/v1/peergroups/find/' as const
export const GET_RANDOM_FEED_PEER_GROUP_URL = '/api/v1/socialfeed/peergroup-relations/' as const
export const RANDOMIZE_FEED_PEER_GROUP_URL = '/api/v1/socialfeed/peergroup-relations/randomize/' as const

// Accounts
export const GET_PROVIDERS_URL = '/api/v1/accounts/providers/' as const
export const GET_PROVIDER_ACCOUNTS_ALL_URL = '/api/v1/accounts/provider-accounts/all/' as const
export const GET_PROVIDER_DETAILS_URL = '/api/v1/accounts/providers/:id/' as const
export const GET_PROVIDER_ACCOUNT_STATE_URL = '/api/v1/accounts/provider-accounts/:id/' as const
export const CREATE_PROVIDER_ACCOUNT_URL = '/api/v1/accounts/provider-accounts/create/' as const
export const UPDATE_PROVIDER_ACCOUNT_URL = '/api/v1/accounts/provider-accounts/:id/update/' as const
export const DELETE_PROVIDER_ACCOUNT_URL = '/api/v1/accounts/provider-accounts/:id/delete/' as const
export const SET_PROVIDER_ACCOUNT_MFA_URL = '/api/v1/accounts/provider-accounts/:id/set-mfa-credentials/' as const
export const CANCEL_PROVIDER_ACCOUNT_MFA_URL = '/api/v1/accounts/provider-accounts/:id/cancel/' as const
export const LINK_PROVIDER_BALANCE_ACCOUNT_URL = '/api/v1/accounts/:id/link/' as const
export const UNLINK_PROVIDER_BALANCE_ACCOUNT_URL = '/api/v1/accounts/:id/unlink/' as const
export const DELETE_PROVIDER_BALANCE_ACCOUNT_URL = '/api/v1/accounts/:id/delete/' as const
export const CREATE_MANUAL_ACCOUNT_URL = '/api/v1/accounts/manual/create/' as const
export const EDIT_MANUAL_ACCOUNT_URL = '/api/v1/accounts/manual/:id/patch/' as const
export const UNLINK_MANUAL_BALANCE_ACCOUNT_URL = '/api/v1/accounts/manual/:id/unlink/' as const
export const DELETE_MANUAL_BALANCE_ACCOUNT_URL = '/api/v1/accounts/manual/:id/delete/' as const
export const FETCH_MISSING_ACCOUNTS_URL = '/api/v1/accounts/provider-accounts/fetch-missing/' as const

// Accounts Live Update
export const GET_LIVE_UPDATE_INFO_URL = '/api/v1/accounts/live-update/info/' as const
export const FLUSH_LIVE_UPDATE_URL = '/api/v1/accounts/live-update/flush/' as const

// Assets
export const GET_MEMBER_ASSETS_BREAKDOWN_URL = '/api/v1/assets/breakdown/' as const
export const GET_PEER_GROUP_ASSETS_BREAKDOWN_URL = '/api/v1/peergroups/assets/breakdown/' as const
export const GET_MEMBER_ASSETS_TREND_URL = '/api/v1/assets/trend/' as const
export const GET_PEER_GROUP_ASSETS_TREND_URL = '/api/v1/peergroups/assets/trend/' as const
export const GET_MEMBER_ASSETS_ACCOUNTS_ALL_URL = '/api/v1/assets/accounts/all/' as const
export const GET_ASSETS_RATES_URL = '/api/v1/assets/rates/' as const

// Cars
export const GET_CAR_MAKES_URL = '/api/v1/car/makes/' as const
export const GET_CAR_MODELS_URL = '/api/v1/car/makes/:make/models/' as const
export const GET_CARS_URL = '/api/v1/member/cars/' as const
export const CREATE_CAR_URL = '/api/v1/member/cars/create/' as const
export const PATCH_CAR_URL = '/api/v1/member/cars/:id/patch/' as const
export const UNLINK_CAR_URL = '/api/v1/member/cars/:id/unlink/' as const
export const DELETE_CAR_URL = '/api/v1/member/cars/:id/delete/' as const

// Credit
export const GET_MEMBER_CREDIT_TREND_URL = '/api/v1/creditscore/trend/' as const
export const GET_PEER_GROUP_CREDIT_TREND_URL = '/api/v1/peergroups/creditscore/trend/' as const
export const GET_CREDIT_TOTALS_URL = '/api/v1/creditscore/totals/' as const
export const GET_CREDIT_ACCOUNTS_URL = '/api/v1/creditscore/accounts/' as const

// Debts
export const GET_MEMBER_DEBTS_BREAKDOWN_URL = '/api/v1/debts/breakdown/' as const
export const GET_PEER_GROUP_DEBTS_BREAKDOWN_URL = '/api/v1/peergroups/debts/breakdown/' as const
export const GET_MEMBER_DEBTS_TREND_URL = '/api/v1/debts/trend/' as const
export const GET_PEER_GROUP_DEBTS_TREND_URL = '/api/v1/peergroups/debts/trend/' as const
export const GET_MEMBER_DEBTS_ACCOUNTS_ALL_URL = '/api/v1/debts/accounts/all/' as const
export const GET_DEBTS_RATES_URL = '/api/v1/debts/rates/' as const

// Device
export const GET_DEVICES_URL = '/api/v1/devices/' as const
export const DELETE_DEVICE_URL = '/api/v1/devices/:id/unverify/' as const

// Events
export const FLUSH_EVENTS_URL = '/api/v1/events/' as const
export const FLUSH_IMPERSONALIZED_EVENTS_URL = '/api/v1/impersonalized-events/' as const
export const FLUSH_POST_IMPRESSIONS_URL = '/api/v1/socialfeed/events/' as const

// Feed
export const GET_FEED_MEMBER_INFO_URL = '/api/v1/socialfeed/user/' as const
export const GET_FEED_AUTHOR_URL = '/api/v1/socialfeed/users/' as const
export const GET_FEED_AUTHOR_PUBLIC_URL = '/api/v1/socialfeed/users/public/' as const
export const GET_FEED_TAG_URL = '/api/v1/socialfeed/tags/:name/' as const
export const GET_FEED_TAG_PUBLIC_URL = '/api/v1/socialfeed/tags/:name/public/' as const
export const FOLLOW_USER_URL = '/api/v1/socialfeed/users/:uid/follow/' as const
export const UNFOLLOW_USER_URL = '/api/v1/socialfeed/users/:uid/unfollow/' as const
export const FOLLOW_POST_URL = '/api/v1/socialfeed/posts/:uid/follow/' as const
export const UNFOLLOW_POST_URL = '/api/v1/socialfeed/posts/:uid/unfollow/' as const
export const LIKE_COMMENT_URL = '/api/v1/socialfeed/comments/:id/like/' as const
export const UNLIKE_COMMENT_URL = '/api/v1/socialfeed/comments/:id/unlike/' as const
export const LIKE_POST_URL = '/api/v1/socialfeed/posts/:uid/like/' as const
export const UNLIKE_POST_URL = '/api/v1/socialfeed/posts/:uid/unlike/' as const
export const REPORT_COMMENT_URL = '/api/v1/socialfeed/comments/:id/report/' as const
export const GET_POSTS_URL = '/api/v1/socialfeed/posts/' as const
export const GET_POSTS_PUBLIC_URL = '/api/v1/socialfeed/posts/public/' as const
export const GET_POST_URL = '/api/v1/socialfeed/posts/:uid/' as const
export const GET_POST_PUBLIC_URL = '/api/v1/socialfeed/posts/:uid/public/' as const
export const GET_COMMENTS_URL = '/api/v1/socialfeed/comments/' as const
export const GET_COMMENTS_PUBLIC_URL = '/api/v1/socialfeed/comments/public/' as const
export const REPORT_POST_URL = '/api/v1/socialfeed/posts/:uid/report/' as const
export const REPORT_AD_POST_URL = '/api/v1/socialfeed/posts/:uid/report_ad/' as const
export const DELETE_POST_URL = '/api/v1/socialfeed/posts/:uid/' as const
export const DELETE_COMMENT_URL = '/api/v1/socialfeed/comments/:id/' as const
export const POST_FEED_POST_URL = '/api/v1/socialfeed/posts/' as const
export const GET_FOLLOWED_POSTS_COUNT_URL = '/api/v1/socialfeed/user/followed_posts/' as const
export const PATCH_FEED_POST_URL = '/api/v1/socialfeed/posts/:uid/' as const
export const POST_FEED_COMMENT_URL = '/api/v1/socialfeed/comments/' as const
export const PATCH_FEED_COMMENT_URL = '/api/v1/socialfeed/comments/:id/' as const
export const GET_FILTERS_STATUS_URL = '/api/v1/socialfeed/filters/status/' as const
export const GET_FEED_USERNAME_EXISTS_URL = '/api/v1/socialfeed/users/check-username-exists/' as const
export const POST_SOCIAL_PROFILE_URL = '/api/v1/socialfeed/user/' as const
export const GET_FOLLOWED_USERS_URL = '/api/v1/socialfeed/user/followed_people/' as const
export const GET_FOLLOWED_TAGS_URL = '/api/v1/socialfeed/user/followed_tags/' as const
export const FOLLOW_TAG_URL = '/api/v1/socialfeed/tags/:tag_name/follow/' as const
export const UNFOLLOW_TAG_URL = '/api/v1/socialfeed/tags/:tag_name/unfollow/' as const
export const SUGGEST_USERS_URL = '/api/v1/socialfeed/users/suggest/' as const
export const GET_TAGS_URL = '/api/v1/socialfeed/tags/' as const

// Homes
export const GET_HOMES_URL = '/api/v1/member/homes/' as const
export const CREATE_HOME_URL = '/api/v1/member/homes/create/' as const
export const PATCH_HOME_URL = '/api/v1/member/homes/:id/patch/' as const
export const UNLINK_HOME_URL = '/api/v1/member/homes/:id/unlink/' as const
export const DELETE_HOME_URL = '/api/v1/member/homes/:id/delete/' as const
export const GET_HOME_PRICE_URL = '/api/v1/house/assess/' as const

// Income
export const GET_MEMBER_INCOME_TREND_URL = '/api/v1/income/trend/' as const
export const GET_PEER_GROUPS_INCOME_TREND_URL = '/api/v1/peergroups/income/trend/' as const
export const GET_MEMBER_INCOME_BREAKDOWN_URL = '/api/v1/income/breakdown/' as const
export const GET_PEER_GROUPS_INCOME_BREAKDOWN_URL = '/api/v1/peergroups/income/breakdown/' as const
export const GET_INCOME_TRACKERS_URL = '/api/v1/income/trackers/' as const
export const CREATE_INCOME_TRACKER_URL = '/api/v1/income/trackers/create/' as const
export const UPDATE_INCOME_TRACKER_URL = '/api/v1/income/trackers/:id/update/' as const
export const DELETE_INCOME_TRACKER_URL = '/api/v1/income/trackers/:id/delete/' as const

// Insights
export const GET_INSIGHTS_URL = '/api/v1/insights/' as const
export const PATCH_INSIGHTS_URL = '/api/v1/insights/patch/' as const
export const GET_SOCIAL_INSIGHTS_URL = '/api/v1/socialfeed/socialinsights/' as const

// Locations
export const SEARCH_LOCATIONS_URL = '/api/v1/locations/generic/' as const
export const SEARCH_CITIES_URL = '/api/v1/locations/cities/' as const

// Member
export const PING_URL = '/api/v1/ping/' as const
export const GET_MEMBER_INFO_URL = '/api/v1/member/info/' as const
export const CREATE_MEMBER_URL = '/api/v1/member/register/' as const
export const DELETE_MEMBER_ACCOUNT_URL = '/api/v1/member/delete/' as const
export const LOGIN_MEMBER_URL = '/api/v1/member/login/' as const
export const LOGOUT_MEMBER_URL = '/api/v1/member/logout/' as const
export const SET_MEMBER_CREDIT_URL = '/api/v1/member/set-pii/' as const
export const SET_MEMBER_CREDIT_EXTENDED_URL = '/api/v1/member/set-pii-extended/' as const
export const SET_MEMBER_ASSETS_URL = '/api/v1/member/set-assets/' as const

export const CHECK_PASSWORD_URL = '/api/v1/member/check-password/'
export const SEND_PASSWORD_RESET_CODE_URL = '/api/v1/member/password/send-reset-code/' as const
export const RESET_PASSWORD_URL = '/api/v1/member/password/change/' as const
export const CHANGE_PASSWORD_URL = '/api/v1/settings/password/change/' as const
export const PATCH_MEMBER_INFO_URL = '/api/v1/member/patch/' as const

export const PATCH_MEMBER_PREFERENCES_URL = '/api/v1/member/preferences/patch/' as const

export const GET_ADVISOR_MEETINGS_INFO_URL = '/api/v1/advisor/meetings/info/' as const

// Meta
export const GET_META_DATA_URL = '/api/v1/meta/' as const

// Networth
export const GET_NETWORTH_SUMMARY_URL = '/api/v1/networth/summary/' as const
export const GET_MEMBER_NETWORTH_TREND_URL = '/api/v1/networth/trend/' as const
export const GET_PEER_GROUP_NETWORTH_TREND_URL = '/api/v1/peergroups/networth/trend/' as const

// Offers
export const GET_OFFER_URL = '/api/v1/offers/:id' as const

// Onboarding
export const CONFIRM_MEMBER_CREDIT_URL = '/api/v1/member/confirm-pii/' as const
export const CHECK_EMAIL_EXISTENCE_URL = '/api/v1/check-email-not-exist/' as const

// Bounty
export const GET_REDEMPTION_ACCOUNT_URL = '/api/v1/redemptions/account'
export const GET_REWARDS_SUMMARY_URL = '/api/v1/rewards/summary/'
export const CREATE_REDEMPTION_URL = '/api/v1/redemptions/'
export const GET_REWARDS_URL = '/api/v1/rewards/'
export const GET_REDEMPTIONS_URL = '/api/v1/redemptions/'
export const PATCH_REWARD_URL = '/api/v1/rewards/:id/patch/'

export const GET_REFERRAL_INFO_URL = '/api/v1/referral/info/'

// Spending
export const GET_MEMBER_SPENDING_TREND_URL = '/api/v1/spending/trend/' as const
export const GET_PEER_GROUPS_SPENDING_TREND_URL = '/api/v1/peergroups/spending/trend/' as const
export const GET_MEMBER_SPENDING_BREAKDOWN_URL = '/api/v1/spending/breakdown/' as const
export const GET_PEER_GROUPS_SPENDING_BREAKDOWN_URL = '/api/v1/peergroups/spending/breakdown/' as const
export const GET_SPENDING_TRACKERS_URL = '/api/v1/spending/trackers/' as const
export const CREATE_SPENDING_TRACKER_URL = '/api/v1/spending/trackers/create/' as const
export const UPDATE_SPENDING_TRACKER_URL = '/api/v1/spending/trackers/:id/update/' as const
export const DELETE_SPENDING_TRACKER_URL = '/api/v1/spending/trackers/:id/delete/' as const

// Transactions
export const GET_TRANSACTIONS_URL = '/api/v1/transactions/' as const
export const PATCH_TRANSACTION_URL = '/api/v1/transactions/:id/patch/' as const
export const PATCH_TRANSACTIONS_URL = '/api/v1/transactions/patch/' as const

// Media
export const POST_MEDIA_IMAGE_URL = '/api/v1/socialfeed/images/upload/' as const
export const POST_MEDIA_LINK_URL = '/api/v1/socialfeed/links/' as const

// Public
export const GET_PUBLIC_TREND_URL = '/api/v1/public-data/trend/' as const
export const GET_PUBLIC_BREAKDOWN_BY_CATEGORY_URL = '/api/v1/public-data/breakdown-by-category/' as const
export const GET_PUBLIC_BREAKDOWN_BY_STATE_URL = '/api/v1/public-data/breakdown-by-state/' as const
export const GET_PUBLIC_BREAKDOWN_BY_MERCHANT_URL = '/api/v1/public-data/breakdown-by-merchant/' as const

// Subscription
export const GET_SUBSCRIPTION_URL = '/api/v1/subscriptions/subscription/' as const
export const CANCEL_SUBSCRIPTION_URL = '/api/v1/subscriptions/subscription/cancel/' as const

// Future
export const GET_FUTURE_URL = '/api/v1/future/' as const
export const CREATE_FUTURE_LAYER_URL = '/api/v1/future/layers/:slug/' as const
export const FUTURE_LAYER_URL = '/api/v1/future/layers/:slug/:uid/' as const
export const ASSUMPTIONS_URL = '/api/v1/future/assumptions/' as const
export const ASSUMPTIONS_HINTS_URL = '/api/v1/future/assumptions/hints/' as const
export const RESET_ASSUMPTIONS_URL = '/api/v1/future/assumptions/reset/' as const
export const POST_LAYER_CHECK_URL = '/api/v1/future/layers/:slug/check/' as const
export const LAYER_HINTS_URL = '/api/v1/future/layers/:slug/hints/' as const
export const COMPLETE_LAYER_URL = '/api/v1/future/layers/:slug/:uid/complete/' as const

// Stripe
export const POST_STRIPE_PAYMENT_METHOD_URL = '/api/v1/subscriptions/stripe/' as const
export const POST_STRIPE_SUBSCRIPTION_REFRESH_URL = '/api/v1/subscriptions/stripe/refresh/' as const

// Persistent Logging
export const SEND_LOGS_URL = '/api/v1/logs/'

// Advisor
export const GET_ADVISORS_URL = '/api/v1/advisor/advisors/' as const
export const GET_ADVISORS_PUBLIC_URL = '/api/v1/advisor/advisors/public/' as const
export const GET_ADVISOR_TIMESLOTS_URL = '/api/v1/advisor/advisors/:advisor_uid/timeslots/' as const
export const GET_ADVISOR_TIMESLOTS_PUBLIC_URL = '/api/v1/advisor/advisors/:advisor_uid/timeslots/public/' as const
export const ADVISOR_MEETINGS_URL = '/api/v1/advisor/meetings/' as const
export const CANCEL_MEETING_URL = '/api/v1/advisor/meetings/:uid/cancel/' as const
export const MEETING_URL = '/api/v1/advisor/meetings/:uid/' as const
export const GET_MEETING_VIDEO_DETAILS_URL = '/api/v1/advisor/meetings/:meeting_uid/video/' as const
export const PATCH_REVIEW_URL = '/api/v1/socialfeed/reviews/ensure/' as const
export const GET_ADVISOR_REVIEWS_URL = '/api/v1/socialfeed/reviews/' as const
export const GET_ADVISOR_PUBLIC_REVIEWS_URL = '/api/v1/socialfeed/reviews/public/' as const
export const SEARCH_ADVISOR_PUBLIC_URL = '/api/v1/advisor/advisors/search/public/' as const

export const GET_ADVISOR_TOP_REVIEWS_URL = '/api/v1/socialfeed/reviews/top/public/' as const
export const GET_TOP_ADVISORS_URL = '/api/v1/advisor/advisors/top/public/' as const


export type GetProvidersApiPayload = { name?: string, popular?: number }

export type GetInsightsApiPayload =
  & RelationsParam
  & PeriodParam
  & { placement: PlacementType[] }
  & InsightCategoriesApiFilter

export type GetFunction =
  // peergroups
  & SimpleUrl<typeof GET_PEER_GROUP_RELATIONS_URL, { items: RawPeergroupRelation[] }>
  & SimpleUrl<typeof GET_RANDOM_FEED_PEER_GROUP_URL, RawPeergroupRelation[]>
  & GetParamsUrl<typeof GET_PEER_GROUP_TOTAL_TOTALS_URL, GroupsWithDateParam, PeergroupsTotals[]>
  & GetParamsUrl<typeof GET_PEER_GROUP_DEBTS_BREAKDOWN_URL, GroupsWithDateParam, RawPeergroupAccountsBreakdown[]>
  & GetParamsUrl<typeof GET_PEER_GROUP_ASSETS_BREAKDOWN_URL, GroupsWithDateParam, RawPeergroupAccountsBreakdown[]>
  & GetParamsUrl<typeof GET_PEER_GROUP_CREDIT_TREND_URL | typeof GET_PEER_GROUP_NETWORTH_TREND_URL, GroupsWithPeriodParam, PeergroupTrend[]>
  // accounts
  & GetParamsUrl<typeof GET_PROVIDERS_URL, GetProvidersApiPayload, BaseProvider[]>
  & SimpleUrl<UrlWithId<typeof GET_PROVIDER_DETAILS_URL>, DetailedProvider>
  & SimpleUrl<typeof GET_PROVIDER_ACCOUNTS_ALL_URL, RawMixedProviderAccount[]>
  & SimpleUrl<UrlWithId<typeof GET_PROVIDER_ACCOUNT_STATE_URL>, Accomplishmentable<RawProviderAccount>>
  // accounts live update
  & SimpleUrl<typeof GET_LIVE_UPDATE_INFO_URL, LiveUpdateInfo>
  // assets
  & SimpleUrl<typeof GET_MEMBER_ASSETS_ACCOUNTS_ALL_URL, RawAllDetailedAccountsDict>
  & GetParamsUrl<typeof GET_ASSETS_RATES_URL, PeriodParam & GroupParam, AccountRate[]>
  & GetParamsUrl<typeof GET_MEMBER_ASSETS_BREAKDOWN_URL, DateParam, AggregatedBalanceByCategory[]>
  // assets and debts
  & GetParamsUrl<typeof GET_MEMBER_ASSETS_TREND_URL | typeof GET_MEMBER_DEBTS_TREND_URL,
    & ScaledPeriodParam & { category: CategoryId | null }, Trend>
  & GetParamsUrl<typeof GET_PEER_GROUP_ASSETS_TREND_URL | typeof GET_PEER_GROUP_DEBTS_TREND_URL,
    & GroupsWithPeriodParam & { category: CategoryId | null }, PeergroupTrend[]>
  // cars
  & SimpleUrl<typeof GET_CARS_URL, RawPaginatable<Car>>
  & SimpleUrl<typeof GET_CAR_MAKES_URL, CarMake[]>
  & SimpleUrl<{ url: typeof GET_CAR_MODELS_URL, params: { make: number } }, CarModel[]>
  // credit
  & GetParamsUrl<typeof GET_CREDIT_TOTALS_URL, DateParam, CreditTotals>
  & GetParamsUrl<typeof GET_CREDIT_ACCOUNTS_URL, DateParam, RawCreditAccount[]>
  // networth
  & GetParamsUrl<typeof GET_NETWORTH_SUMMARY_URL, NetworthFilterPreferencesApiPayload & { excluded_account_uids: string | null }, NetworthSummaryResponse>
  & GetParamsUrl<typeof GET_MEMBER_CREDIT_TREND_URL | typeof GET_MEMBER_NETWORTH_TREND_URL, ScaledPeriodParam, Trend>
  // debts
  & SimpleUrl<typeof GET_MEMBER_DEBTS_ACCOUNTS_ALL_URL, RawAllDetailedAccountsDict>
  & GetParamsUrl<typeof GET_DEBTS_RATES_URL, PeriodParam & GroupParam, AccountRate[]>
  & GetParamsUrl<typeof GET_MEMBER_DEBTS_BREAKDOWN_URL, DateParam, AggregatedBalanceByCategory[]>
  // devices
  & SimpleUrl<typeof GET_DEVICES_URL, Device[]>
  // homes
  & SimpleUrl<typeof GET_HOMES_URL, RawPaginatable<Home>>
  // income
  & GetParamsUrl<typeof GET_MEMBER_INCOME_TREND_URL,
    & ScaledPeriodParam & { category: CategoryId | null }, Trend>
  & GetParamsUrl<typeof GET_PEER_GROUPS_INCOME_TREND_URL,
    & GroupsWithPeriodParam & { category: CategoryId | null }, PeergroupTrend[]>
  & GetParamsUrl<typeof GET_MEMBER_INCOME_BREAKDOWN_URL, PeriodParam, MoneyflowBreakdownItem[]>
  & GetParamsUrl<typeof GET_PEER_GROUPS_INCOME_BREAKDOWN_URL, GroupsWithPeriodParam, MoneyflowPeergroupBreakdownResponseItem[]>
  & SimpleUrl<typeof GET_INCOME_TRACKERS_URL, MoneyflowTracker[]>
  // insights
  & GetParamsUrl<
    & typeof GET_INSIGHTS_URL,
    GetInsightsApiPayload,
    & Insight[]
  >
  & GetParamsUrl<typeof GET_SOCIAL_INSIGHTS_URL, GetSocialInsightsApiPayload, { items: RawSocialInsight[] }>
  // locations
  & GetParamsUrl<typeof SEARCH_LOCATIONS_URL, { name: string }, Location[]>
  & GetParamsUrl<typeof SEARCH_CITIES_URL, { name: string }, City[]>
  // members
  & SimpleUrl<typeof GET_MEMBER_INFO_URL, RawMemberWithProps>
  & SimpleUrl<typeof GET_ADVISOR_MEETINGS_INFO_URL, Pick<Member, 'is_meeting_eligible' | 'had_appointed_meeting'>>
  // meta
  & SimpleUrl<typeof GET_META_DATA_URL, RawMetaData>
  // offers
  & SimpleUrl<{ url: typeof GET_OFFER_URL, params: { id: string } }, Offer>
  // bounty
  & SimpleUrl<typeof GET_REDEMPTION_ACCOUNT_URL, GetRedemptionAccountResponse>
  & SimpleUrl<typeof GET_REWARDS_SUMMARY_URL, GetRewardSummaryResponse>
  & SimpleUrl<typeof GET_REFERRAL_INFO_URL, ReferralInfo>
  // spending
  & GetParamsUrl<typeof GET_MEMBER_SPENDING_TREND_URL,
    & ScaledPeriodParam & { category: CategoryId | null }, Trend>
  & GetParamsUrl<typeof GET_PEER_GROUPS_SPENDING_TREND_URL,
    & GroupsWithPeriodParam & { category: CategoryId | null }, PeergroupTrend[]>
  & GetParamsUrl<typeof GET_MEMBER_SPENDING_BREAKDOWN_URL, PeriodParam, MoneyflowBreakdownItem[]>
  & GetParamsUrl<typeof GET_PEER_GROUPS_SPENDING_BREAKDOWN_URL, GroupsWithPeriodParam, MoneyflowPeergroupBreakdownResponseItem[]>
  & SimpleUrl<typeof GET_SPENDING_TRACKERS_URL, MoneyflowTracker[]>
  // transactions
  & GetParamsUrl<typeof GET_TRANSACTIONS_URL, GetTransactionsApiPayload, RawPaginatable<RawTransaction> | RawTransaction[]>
  // rewards
  & GetParamsUrl<typeof GET_REWARDS_URL, GetRewardsApiPayload, RawPaginatable<RewardsHistoryItem>>
  // redemptions
  & GetParamsUrl<typeof GET_REDEMPTIONS_URL, GetRedemptionsApiPayload, RawPaginatable<Redemption>>
  // feed
  & SimpleUrl<typeof GET_FEED_MEMBER_INFO_URL, RawFeedUser>
  & SimpleUrl<typeof GET_FOLLOWED_POSTS_COUNT_URL, FollowedPostsCount>
  & GetParamsUrl<typeof GET_FEED_AUTHOR_URL, { username: string }, FeedUser>
  & GetParamsUrl<typeof GET_FEED_AUTHOR_PUBLIC_URL, { username: string }, FeedUser>
  & SimpleUrl<{ url: typeof GET_FEED_TAG_URL, params: { name: string } }, FeedTag>
  & SimpleUrl<{ url: typeof GET_FEED_TAG_PUBLIC_URL, params: { name: string } }, FeedTag>
  & SimpleUrl<{ url: typeof GET_POST_URL, params: { uid: PostUid } }, RawPost>
  & SimpleUrl<{ url: typeof GET_POST_PUBLIC_URL, params: { uid: PostUid } }, RawPost>
  & GetParamsUrl<typeof GET_POSTS_URL, GetPostsApiPayload, RawPaginatable<RawPost>>
  & GetParamsUrl<typeof GET_POSTS_PUBLIC_URL, GetPostsApiPayload, RawPaginatable<RawPost>>
  & GetParamsUrl<typeof GET_COMMENTS_URL, GetCommentsApiPayload, RawPaginatable<RawComment>>
  & GetParamsUrl<typeof GET_COMMENTS_PUBLIC_URL, GetCommentsApiPayload, RawPaginatable<RawComment>>
  & GetParamsUrl<typeof GET_FEED_USERNAME_EXISTS_URL, CheckExistsRequest, CheckExistsResponse>
  & GetParamsUrl<typeof GET_FOLLOWED_USERS_URL, GetFollowedUsersApiPayload, RawPaginatable<RawFollowedUser>>
  & GetParamsUrl<typeof GET_FOLLOWED_TAGS_URL, GetFollowedTagsApiPayload, RawPaginatable<FeedTag>>
  & GetParamsUrl<typeof SUGGEST_USERS_URL, SuggestUsersApiPayload, { items: SuggestUser[] }>
  & GetParamsUrl<typeof GET_TAGS_URL, GetFeedTagsRequest, { items: SuggestTag[] }>
  // public
  & GetParamsUrl<typeof GET_PUBLIC_TREND_URL, PublicTrendApiPayload, PublicTrendState>
  & GetParamsUrl<typeof GET_PUBLIC_BREAKDOWN_BY_CATEGORY_URL, PublicBreakdownByCategoryApiPayload, PublicBreakdownWithTotal>
  & GetParamsUrl<typeof GET_PUBLIC_BREAKDOWN_BY_STATE_URL, PublicBreakdownByStateApiPayload, PublicBreakdownWithTotal>
  & GetParamsUrl<typeof GET_PUBLIC_BREAKDOWN_BY_MERCHANT_URL, PublicBreakdownByMerchantApiPayload, PublicBreakdown>
  // Subscription
  & SimpleUrl<typeof GET_SUBSCRIPTION_URL, ExtendedSubscription>
  // Future
  & SimpleUrl<typeof GET_FUTURE_URL, GetFutureApiResponse>
  & SimpleUrl<typeof ASSUMPTIONS_URL, FutureAssumptions>
  // Advisor
  & GetParamsUrl<typeof GET_ADVISORS_URL, { topics: AdvisorTopics[], advisor_uid?: AdvisorUid } & OffsetPaginationApiPayload, RawPaginatable<AdvisorApiOutput>>
  & GetParamsUrl<typeof GET_ADVISORS_PUBLIC_URL, { topics: AdvisorTopics[], income: number, zip5: string } & OffsetPaginationApiPayload, RawPaginatable<AdvisorApiOutput>>
  & GetParamsUrl<typeof GET_ADVISOR_REVIEWS_URL, GetPublicAdvisorReviewsPayload & Partial<PaginatableApiPayload & OrderingApiPayload>, RawPaginatable<RawAdvisorReview>>
  & GetParamsUrl<typeof GET_ADVISOR_PUBLIC_REVIEWS_URL, GetPublicAdvisorReviewsPayload & Partial<PaginatableApiPayload & OrderingApiPayload>, RawPaginatable<RawAdvisorReview>>
  & SimpleUrl<typeof ADVISOR_MEETINGS_URL, MeetingApiOutput[]>
  & SimpleUrl<{ url: typeof GET_MEETING_VIDEO_DETAILS_URL, params: { meeting_uid: string }}, MeetingVideoDetailsApiOutput>
  & SimpleUrl<{ url: typeof MEETING_URL, params: { uid: string }}, MeetingApiOutput>
  & SimpleUrl<{ url: typeof GET_ADVISOR_TIMESLOTS_URL, params: { advisor_uid: string }}, RawPaginatable<string>>
  & SimpleUrl<{ url: typeof GET_ADVISOR_TIMESLOTS_PUBLIC_URL, params: { advisor_uid: string }}, RawPaginatable<string>>
  & GetParamsUrl<typeof SEARCH_ADVISOR_PUBLIC_URL, { username: string }, AdvisorWithRatingBreakdown>

  & SimpleUrl<typeof GET_ADVISOR_TOP_REVIEWS_URL, { items: RawAdvisorReview[] }>
  & SimpleUrl<typeof GET_TOP_ADVISORS_URL, { items: Advisor[] }>

  & CryptoRewardsGetEndpoints
  & CreditCardGetEndpoints

export type FormUrls =
  | typeof CREATE_INCOME_TRACKER_URL
  | typeof CREATE_SPENDING_TRACKER_URL
  | UrlWithId<typeof UPDATE_SPENDING_TRACKER_URL | typeof UPDATE_INCOME_TRACKER_URL>
  | typeof CREATE_MEMBER_URL
  | typeof LOGIN_MEMBER_URL
  | typeof SET_MEMBER_ASSETS_URL
  | typeof SET_MEMBER_CREDIT_EXTENDED_URL
  | typeof SET_MEMBER_CREDIT_URL
  | typeof CREATE_MANUAL_ACCOUNT_URL
  | UrlWithId<typeof EDIT_MANUAL_ACCOUNT_URL>
  | typeof CREATE_PEER_GROUP_URL
  | UrlWithId<typeof UPDATE_PEER_GROUP_URL>
  | typeof SEND_PASSWORD_RESET_CODE_URL
  | typeof RESET_PASSWORD_URL
  | typeof CHANGE_PASSWORD_URL
  | typeof CREATE_HOME_URL
  | typeof CREATE_CAR_URL
  | UrlWithId<typeof PATCH_HOME_URL | typeof PATCH_CAR_URL>
  | typeof PATCH_MEMBER_INFO_URL
  | typeof CHANGE_PASSWORD_URL
  | UrlWithId<typeof PATCH_TRANSACTION_URL>
  | typeof PATCH_TRANSACTIONS_URL
  | typeof CREATE_REDEMPTION_URL
  | typeof POST_SOCIAL_PROFILE_URL


export type PostFunction =
  // forms
  & PostBodyUrl<FormUrls, AnyDict, AnyDict>
  // events
  & PostBodyUrl<typeof FLUSH_EVENTS_URL, { events: PlainEvent[] }, AnyObj>
  & PostBodyUrl<typeof FLUSH_IMPERSONALIZED_EVENTS_URL, { events: PlainEvent[] }, AnyObj>
  & PostBodyUrl<typeof FLUSH_POST_IMPRESSIONS_URL, PostImpressionsArray, AnyObj>
  // peergroups
  & SimpleUrl<typeof RANDOMIZE_PEER_GROUP_URL, RawPeergroupRelation>
  & SimpleUrl<typeof RANDOMIZE_FEED_PEER_GROUP_URL, RawPeergroupRelation>
  & SimpleUrl<UrlWithId<typeof DELETE_PEER_GROUP_URL>, AnyObj>
  & PostBodyUrl<typeof FIND_PEER_GROUP_URL, { [name: string]: string }, Peergroup>
  // accounts
  & SimpleUrl<typeof FETCH_MISSING_ACCOUNTS_URL, {}>
  & PostBodyUrl<typeof CREATE_PROVIDER_ACCOUNT_URL, CreateProviderAccountApiPayload, RawProviderAccount>
  & PostBodyUrl<UrlWithId<typeof UPDATE_PROVIDER_ACCOUNT_URL>, EncryptedCredentials, RawProviderAccount>
  & SimpleUrl<UrlWithId<typeof CANCEL_PROVIDER_ACCOUNT_MFA_URL>, RawProviderAccount>
  & SimpleUrl<UrlWithId<typeof DELETE_PROVIDER_ACCOUNT_URL>, AnyObj>
  & PostBodyUrl<UrlWithId<typeof SET_PROVIDER_ACCOUNT_MFA_URL>, EncryptedCredentials, RawProviderAccount>
  & SimpleUrl<UrlWithId<typeof LINK_PROVIDER_BALANCE_ACCOUNT_URL>, RawProviderBalanceAccount>
  & SimpleUrl<UrlWithId<typeof UNLINK_PROVIDER_BALANCE_ACCOUNT_URL>, RawProviderBalanceAccount>
  & SimpleUrl<UrlWithId<typeof DELETE_PROVIDER_BALANCE_ACCOUNT_URL>, MixedAccountOperationApiResponse>
  & SimpleUrl<UrlWithId<typeof UNLINK_MANUAL_BALANCE_ACCOUNT_URL>, RawManualBalanceAccount>
  & SimpleUrl<UrlWithId<typeof DELETE_MANUAL_BALANCE_ACCOUNT_URL>, MixedAccountOperationApiResponse>
  // accounts live update
  & SimpleUrl<typeof FLUSH_LIVE_UPDATE_URL, Accomplishmentable<LiveUpdateInfoByStatus<'flushed'>>>
  // feed
  & SimpleUrl<UrlWithId<typeof FOLLOW_USER_URL>, AnyObj>
  & SimpleUrl<UrlWithId<typeof UNFOLLOW_USER_URL>, AnyObj>
  & SimpleUrl<UrlWithId<typeof FOLLOW_POST_URL>, AnyObj>
  & SimpleUrl<UrlWithId<typeof UNFOLLOW_POST_URL>, AnyObj>
  & SimpleUrl<UrlWithId<typeof LIKE_POST_URL>, CountData>
  & SimpleUrl<UrlWithId<typeof UNLIKE_POST_URL>, CountData>
  & SimpleUrl<UrlWithId<typeof LIKE_COMMENT_URL>, CountData>
  & SimpleUrl<UrlWithId<typeof UNLIKE_COMMENT_URL>, CountData>
  & SimpleUrl<UrlWithId<typeof REPORT_COMMENT_URL>, AnyObj>
  & SimpleUrl<UrlWithId<typeof REPORT_POST_URL>, AnyObj>
  & PostBodyUrl<UrlWithId<typeof REPORT_AD_POST_URL>, { insight_id: InsightId }, AnyObj>
  & PostBodyUrl<typeof POST_FEED_POST_URL, FeedPostInput, RawPost>
  & PostBodyUrl<typeof POST_FEED_COMMENT_URL, FeedCommentInput, RawOwnComment>
  & PostBodyUrl<typeof GET_FILTERS_STATUS_URL, { items: FeedFilterQuery[] }, { items: FeedFilterStatus[] }>
  & SimpleUrl<{ url: typeof FOLLOW_TAG_URL, params: { tag_name: FeedTagName } }, AnyObj>
  & SimpleUrl<{ url: typeof UNFOLLOW_TAG_URL, params: { tag_name: FeedTagName } }, AnyObj>
  // member
  & SimpleUrl<typeof PING_URL, AnyObj>
  & SimpleUrl<typeof DELETE_MEMBER_ACCOUNT_URL, AnyObj>
  & SimpleUrl<typeof LOGOUT_MEMBER_URL, AnyObj>
  & PostBodyUrl<typeof CHECK_PASSWORD_URL, { password: string }, PasswordStrengthPayload>
  & PostBodyUrl<typeof PATCH_MEMBER_PREFERENCES_URL, MemberPreferencesApiPayload, { data: MemberPreferencesApiResponse }>
  // devices
  & SimpleUrl<UrlWithId<typeof DELETE_DEVICE_URL>, AnyObj>
  // cars
  & PostBodyUrl<typeof CREATE_CAR_URL, RawCarFormValues, Car>
  & PostBodyUrl<UrlWithId<typeof PATCH_CAR_URL>, RawCarFormValues, Car>
  & SimpleUrl<UrlWithId<typeof UNLINK_CAR_URL>, AnyObj>
  & SimpleUrl<UrlWithId<typeof DELETE_CAR_URL>, AnyObj>
  // homes
  & PostBodyUrl<typeof CREATE_HOME_URL, RawHomeFormValues, Home>
  & PostBodyUrl<UrlWithId<typeof PATCH_HOME_URL>, RawHomeFormValues, Home>
  & SimpleUrl<UrlWithId<typeof UNLINK_HOME_URL>, AnyObj>
  & SimpleUrl<UrlWithId<typeof DELETE_HOME_URL>, AnyObj>
  & PostBodyUrl<typeof GET_HOME_PRICE_URL, HomeFormValues, HomePriceResponse>
  // insights
  & PostQueryBodyUrl<typeof PATCH_INSIGHTS_URL, { id: number[] }, { is_dismissed: boolean }, AnyObj>
  // onboarding
  & PostBodyUrl<typeof CONFIRM_MEMBER_CREDIT_URL, { answers: CreditAnswer[] }, Accomplishmentable<ConfirmCreditFormData>>
  & PostBodyUrl<typeof CHECK_EMAIL_EXISTENCE_URL, { email: string }, AnyObj>
  // spending
  & PostBodyUrl<UrlWithId<typeof UPDATE_SPENDING_TRACKER_URL>, { limit: string }, Tracker>
  & SimpleUrl<UrlWithId<typeof DELETE_SPENDING_TRACKER_URL>, AnyObj>
  // income
  & PostBodyUrl<UrlWithId<typeof UPDATE_INCOME_TRACKER_URL>, { limit: string }, Tracker>
  & SimpleUrl<UrlWithId<typeof DELETE_INCOME_TRACKER_URL>, AnyObj>
  // transactions
  & PostBodyUrl<UrlWithId<typeof PATCH_TRANSACTION_URL>, PatchTransactionAPIPayload, PatchTransactionResponse>
  // bounty
  & PostBodyUrl<UrlWithId<typeof PATCH_REWARD_URL>, Pick<Reward, 'is_dismissed'>, Reward>
  & PostBodyUrl<typeof CREATE_REDEMPTION_URL, RedemptionFormValues, RedemptionFormResponse>
  // Media
  & PostBodyUrl<typeof POST_MEDIA_IMAGE_URL, FormData, ImageURLs>
  & PostBodyUrl<typeof POST_MEDIA_LINK_URL, { url: string }, MediaLink>
  // Stripe
  & PostBodyUrl<typeof POST_STRIPE_PAYMENT_METHOD_URL, PostSubscriptionStripeApiPayload, Subscription>
  // Subscription
  & PostBodyUrl<typeof CANCEL_SUBSCRIPTION_URL, AnyObj, Subscription>
  // Persistent Logging
  & PostBodyUrl<typeof SEND_LOGS_URL, { data: string, device_id: string }, { webLink: string, s3Link: string }>
  // Future
  & PostBodyUrl<UrlWithId<typeof CREATE_FUTURE_LAYER_URL>, FutureLayerFields, GetFutureApiResponse>
  & PostBodyUrl<{ url: typeof CREATE_FUTURE_LAYER_URL, params: { slug: string }}, FutureLayerFields, GetFutureApiResponse>
  & PostBodyUrl<{ url: typeof POST_LAYER_CHECK_URL, params: { slug: string }}, Partial<FutureLayer>, FutureLayerCheckResult>
  & PostBodyUrl<{ url: typeof LAYER_HINTS_URL, params: { slug: FutureLayerSlug }}, Partial<FutureLayer>, FutureLayerDefinedHints['hints']>
  & SimpleUrl<typeof RESET_ASSUMPTIONS_URL, GetFutureApiResponse>
  & PostBodyUrl<typeof ASSUMPTIONS_HINTS_URL, Partial<FutureAssumptions>, FutureAssumptionsTaxHints>
  & PostBodyUrl<{ url: typeof COMPLETE_LAYER_URL, params: { slug: FutureLayerSlug, uid: FutureLayerUID }}, CompleteLayer, GetFutureApiResponse>
  // Advisor
  & PostBodyUrl<typeof ADVISOR_MEETINGS_URL, CreateAdvisorMeetingApiInput, MeetingApiOutput>
  & PostBodyUrl<{ url: typeof CANCEL_MEETING_URL, params: { uid: MeetingUid }}, AnyObj, MeetingApiOutput>
  & PostBodyUrl<typeof PATCH_REVIEW_URL, AdvisorsPatchReviewPayload, RawAdvisorReview>
  & CryptoRewardsPostEndpoints

  // Credit card onboarding
  & CreditCardPostEndpoints

export type DeleteFunction =
  // feed
  & SimpleUrl<UrlWithId<typeof DELETE_POST_URL>, AnyObj>
  & SimpleUrl<UrlWithId<typeof DELETE_COMMENT_URL>, AnyObj>
  // Future
  & SimpleUrl<UrlWithId<typeof FUTURE_LAYER_URL>, GetFutureApiResponse>
  // Advisor
  & SimpleUrl<UrlWithId<typeof MEETING_URL>, AnyObj>

export type PatchFunction =
  // feed
  & PostBodyUrl<UrlWithId<typeof PATCH_FEED_POST_URL>, Partial<FeedPostInput>, RawPost>
  & PostBodyUrl<UrlWithId<typeof PATCH_FEED_COMMENT_URL>, Partial<FeedCommentInput>, RawOwnComment>
  // Stripe
  & PostBodyUrl<typeof POST_STRIPE_PAYMENT_METHOD_URL, Partial<PostSubscriptionStripeApiPayload>, Subscription>
  // Future
  & PostBodyUrl<{ url: typeof FUTURE_LAYER_URL, params: { slug: FutureLayerSlug, uid: FutureLayerUID }}, Partial<FutureLayer>, GetFutureApiResponse>
  & PostBodyUrl<typeof ASSUMPTIONS_URL, Partial<FutureAssumptions>, GetFutureApiResponse>

  // Credit card onboarding
  & CreditCardPatchEndpoints

